import React, { useEffect, useRef, useState } from 'react';
import { getJson, LinksContainer, loadScript, pathCombine, scrollTo, translate, URLX } from '@polarnopyret/scope';
import { styled, StyledProps } from '@glitz/react';
import { checkoutPageUrl } from 'Shared/known-urls';
import { SolidButton, ButtonTheme } from 'Shared/TextButtons';
import Option from './Option.type';
import ShippingMethodsViewModelType from './ShippingMethodsViewModel.type';
import ShippingMethodViewModelType from './ShippingMethodViewModel.type';
import * as style from 'Shared/Style';

const UNIFAUN_SCRIPT = 'https://api.unifaun.com/rs-extapi/v1/delivery-checkouts-widget/unifaun-checkout-all.min.js';
const UNIFAUN_LOGOS = 'https://api.unifaun.com/rs-extapi/v1/delivery-checkouts-widget/logos';

const NextStepNav = styled.div({
  marginTop: '16px'
});

const StyledErrorMessage = styled.div({
  color: style.colors.negativeColors.negative,
  userSelect: 'none',
  fontSize: '16px',
  padding: {
    y: '12px'
  }
});

type PropType = StyledProps & {
  postalCode: string;
  subTotal: number;
  onSelectShippingMethod: (method: ShippingMethodViewModelType) => void;
  onNextStep: () => void;
  buttonText: string;
  //labelText: string;
  language: string;
  onError: (lock: boolean) => void;
  updateErrorMessage: string;
  updateInvalid: boolean;
};

const ShippingMethods = ({ postalCode, subTotal, language, buttonText, onSelectShippingMethod, onNextStep, onError, updateErrorMessage, updateInvalid }: PropType) => {
  const [isValid, setIsValid] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  //  const [shipmentError, setShipmentError] = useState('');

  const [isShippingInitialized, setIsShippingInitialized] = useState<boolean>(false);
  const [selectedPostalCode, setSelectedPostalCode] = useState<string>(null);
  const [currentSubTotal, setCurrentSubTotal] = useState<number>(null);
  const [shippingMethods, setShippingMethods] = useState<ShippingMethodsViewModelType>(null);

  const widgetRef = useRef<any>(null);
  const shippingOptionsRef = useRef<Option[]>([]);
  const selectedOptionIdRef = useRef<string>(null);
  const selectedOptionPriceRef = useRef<number>(null);
  const selectedAgentIdRef = useRef<string>(null);

  const containerElement = useRef<HTMLDivElement>(null);
  const widgetContainer = useRef<HTMLDivElement>(null);

  const flattenOptions = (options: Option[]): Option[] => {
    const flattenFunction = (acc: Option[], option: Option): Option[] => {
      if (option.subOptions && option.subOptions.length > 0) {
        return acc.concat(option.subOptions.reduce(flattenFunction, []));
      }
      return acc.concat(option);
    };
    return options.reduce(flattenFunction, []);
  };

  const shouldUpdate = (data: any): boolean => {
    const isSameOption =
      data.selectedOptionId === selectedOptionIdRef.current && data.price === selectedOptionPriceRef.current;
    if (!isSameOption) return true;
    return data.agent !== selectedAgentIdRef.current;
  };

  const getShippingMethods = async (postalCode: string): Promise<ShippingMethodsViewModelType> => {
    try {
      const url = new URLX(pathCombine(checkoutPageUrl(), 'getshippingmethods'));
      url.searchParams.set('postalCode', postalCode);
      const result: ShippingMethodsViewModelType = await getJson(url);
      return result;
    } catch (e) {
      setErrorMessage(translate('/Checkout/PostalCode/Errors/Invalid'));
      setShowError(true);
      onError(true);
      return null;
    }
  };

  const loadWidget = async (postalCode: string, subTotal: number): Promise<void> => {
    try {
      const shippingMethodList = (shippingMethods && selectedPostalCode === postalCode && currentSubTotal === subTotal) ? shippingMethods : await getShippingMethods(postalCode);
      if (
        shippingMethodList &&
        shippingMethodList.nShiftShippingMethods &&
        shippingMethodList.nShiftShippingMethods.options &&
        shippingMethodList.nShiftShippingMethods.options.length > 0
      ) {
        setShippingMethods(shippingMethodList);
        const subOptions = shippingMethodList.nShiftShippingMethods.options[0].subOptions;
        if (subOptions && subOptions.length > 0) {
          subOptions.forEach((o) => (o.id = o.id.substr(0, 36)));
        }
        shippingOptionsRef.current = flattenOptions(shippingMethodList.nShiftShippingMethods.options);
        widgetRef.current && widgetRef.current.enable();
        widgetRef.current && widgetRef.current.updateList(shippingMethodList.nShiftShippingMethods);
        setIsValid(true);
        setShowError(false);
        setErrorMessage('');

        return Promise.resolve();
      }

      widgetRef.current && widgetRef.current.disable();
      onSelectShippingMethod({ selectedOptionId: null } as ShippingMethodViewModelType);
      selectedAgentIdRef.current = null;
      selectedOptionIdRef.current = null;
      setIsValid(false);
      setShowError(true);

      setErrorMessage(shippingMethodList?.errorMessage);
      if (containerElement.current) {
        scrollTo(containerElement.current.offsetTop, 300);
      }
      return Promise.reject(null);
    } catch (e) {
      onError(true);
      return Promise.reject(e);
    }
  };

  const initWidget = async () => {
    try {
      await loadScript(UNIFAUN_SCRIPT);
    } catch (error) {
      const alternativeScript = window.location.origin + '/ClientResources/Unifaun/unifaun-checkout-all.min.js';
      await loadScript(alternativeScript);
    }

    const config = {
      language,
      installCSS: 'nshift-v2',
      resultCallback: (data: any) => {
        if (shouldUpdate(data)) {
          const selectedOption = shippingOptionsRef.current.find((x) => x.id === data.selectedOptionId);
          selectedOptionIdRef.current = data.selectedOptionId;
          selectedOptionPriceRef.current = data.price;
          selectedAgentIdRef.current = data.agent;

          if (selectedOption) {
            data.serviceId = selectedOption.serviceId;
            data.carrierId = selectedOption.carrierId;
            data.name = selectedOption.name;
            const selectedAgent = selectedOption.agents.find((a) => a.id === data.agent);
            data.agentName = selectedAgent && selectedAgent.name;
            if (selectedOption.optionIdSecondPart) {
              data.selectedOptionId = `${selectedOption.optionIdFirstPart}+${selectedOption.optionIdSecondPart}`;
            }
          }

          onSelectShippingMethod({
            agentId: data.agent,
            agentName: data.agentName,
            carrierId: data.carrierId,
            name: data.name,
            postalCode: data.postalCode,
            serviceId: data.serviceId,
            selectedOptionId: data.selectedOptionId,
          } as ShippingMethodViewModelType);
        }
      },

      useIcons: true,
      iconsBaseUrl: UNIFAUN_LOGOS,
      iconsInFront: true,
      narrowDisplay: true,
      ultraNarrowBreakpointWidth: 400,
    };

    widgetRef.current = window.UnifaunCheckout && window.UnifaunCheckout.createAt(widgetContainer.current, config);

    // try {
    //   if (shippingAddress && shippingAddress.postalCode) {
    //     await loadWidget(shippingAddress.postalCode);
    //   } else {
    //     onError(true);
    //     widgetRef && widgetRef.current && widgetRef.current.disable();
    //   }
    // } catch (e) {
    //   console.log(e);
    // }
  };

  useEffect(() => {
    if (!isShippingInitialized) {
      setIsShippingInitialized(true);
      initWidget();
    }
  }, [initWidget]);

  useEffect(() => {
    if (postalCode) {
      setSelectedPostalCode(postalCode);
      setCurrentSubTotal(subTotal);
      loadWidget(postalCode, subTotal);
    } else {
      setSelectedPostalCode("Empty");
    }
  }, [postalCode, subTotal]);

  return (
    <div ref={containerElement}>
      <LinksContainer>
        <styled.Div
          ref={widgetContainer}
          css={{
            display: isValid ? 'block' : 'none',
          }}
        />
      </LinksContainer>
      {showError && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
      {updateErrorMessage && <StyledErrorMessage>{updateErrorMessage}</StyledErrorMessage>}
      <NextStepNav>
        <SolidButton
          disabled={updateInvalid}
          onClick={(e) => {
            e.preventDefault();
            onNextStep();
          }}
          theme={ButtonTheme.BlackToWhite}
        >
          {buttonText}
        </SolidButton>
      </NextStepNav>
    </div >
  );
};

export default styled(ShippingMethods);
